 // src/Components/AllProducts.jsx
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseurl } from "../config/url";

/** Pick API base: localhost -> local API, otherwise whatever you set in config/url */
const isLocal =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

const API_BASE = isLocal ? "http://localhost:5000" : baseurl;

/* ----------------- Helpers ----------------- */
const joinUrl = (base, path) => {
  if (!path) return base || "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = (base || "").replace(/\/+$/, "");
  const p = String(path).startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
};

const toSrc = (imageField) => {
  if (!imageField) return null;
  if (typeof imageField === "string") return joinUrl(API_BASE, imageField);
  if (imageField?.url) return joinUrl(API_BASE, imageField.url);
  return null;
};

const formatINR = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const normalizeListResponse = (data) => {
  if (data && typeof data === "object" && "products" in data) {
    const items = Array.isArray(data.products) ? data.products : [];
    return { items, success: data.success !== false };
  }
  if (Array.isArray(data)) return { items: data, success: true };
  return { items: [], success: false };
};

/* ----------------- Component ----------------- */
const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const navigate = useNavigate();

  const goToDetails = useCallback(
    (id) => id && navigate(`/product/details/${id}`),
    [navigate]
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      // Possible endpoints to try
      const candidates = [
        "/api/product/all",
        "/api/product",
        "/api/products",
        "/api/products/all",
      ];

      let data = null;
      let lastTried = "";

      for (const path of candidates) {
        const url = joinUrl(API_BASE, path);
        lastTried = url;
        try {
          const res = await axios.get(url, {
            params: { page: 1, limit: 200 },
            headers,
            validateStatus: (s) => s >= 200 && s < 500,
          });

          if (res.status >= 200 && res.status < 300) {
            data = res.data;
            break;
          } else {
            console.warn(`[AllProducts] ${url} responded ${res.status}`);
          }
        } catch (e) {
          console.error(`[AllProducts] request failed: ${url}`, e?.message);
        }
      }

      if (!data) {
        throw new Error(
          `No list endpoint responded at API base: ${API_BASE}. ` +
            `Check CORS and routes. Last tried: ${lastTried}`
        );
      }

      const norm = normalizeListResponse(data);
      if (!norm.success) throw new Error("API did not return success.");

      setProducts(norm.items.filter(Boolean));
    } catch (e) {
      setErr(e?.message || "Failed to load products.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const marqueeItems =
    products.length > 0 ? [...products, ...products, ...products] : [];

  return (
    <div className="bg-white">
      {/* Inline styles for marquee */}
      <style>
        {`
          @keyframes marquee-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .marquee-container { overflow: hidden; position: relative; }
          .marquee-track {
            display: inline-flex;
            white-space: nowrap;
            will-change: transform;
            animation: marquee-left linear infinite;
          }
          .marquee-container:hover .marquee-track,
          .marquee-container:active .marquee-track,
          .marquee-container:focus-within .marquee-track {
            animation-play-state: paused;
          }
          @media (prefers-reduced-motion: reduce) {
            .marquee-track {
              animation: none !important;
              transform: translateX(0) !important;
            }
          }
        `}
      </style>

      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 py-2 sm:py-3">
          <h1 className="text-base sm:text-lg font-semibold text-slate-800">
            Our Products
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-2 sm:px-3 py-3 sm:py-4">
        {err && (
          <div className="mb-2 p-2 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-xs sm:text-sm">
            {err}
          </div>
        )}

        {loading ? (
          /* Skeleton loader */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-sm border border-slate-100 p-2 sm:p-3 animate-pulse"
              >
                <div className="aspect-[4/3] w-full bg-slate-100 rounded-lg mb-2" />
                <div className="h-3 bg-slate-100 rounded w-4/5 mb-1.5" />
                <div className="h-3 bg-slate-100 rounded w-2/5" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          /* No products found */
          <div className="text-center py-10">
            <p className="text-slate-600 mt-1 text-sm">No products found.</p>
          </div>
        ) : (
          /* Products Marquee */
          <div className="marquee-container rounded-lg border border-slate-100 shadow-sm">
            <div
              className="marquee-track"
              style={{
                animationDuration: `${Math.max(
                  18,
                  Math.min(60, marqueeItems.length * 1.2)
                )}s`,
              }}
            >
              {marqueeItems.map((p, idx) => {
                const img = toSrc(p?.image);
                const name = p?.name || "Unnamed Product";
                const price =
                  typeof p?.price === "number" ? p.price : null;

                return (
                  <button
                    key={`${p?._id || name}-${idx}`}
                    onClick={() => goToDetails(p?._id)}
                    className="w-[65vw] xs:w-[50vw] sm:w-[34vw] md:w-[26vw] lg:w-[21vw] xl:w-[17vw] m-1 sm:m-2 rounded-lg border border-sky-100 bg-white shadow-sm flex-shrink-0 flex flex-col items-center justify-between text-center p-2 sm:p-3 gap-1.5 transition hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-sky-400"
                    aria-label={`View ${name}`}
                    type="button"
                  >
                    {/* Image */}
                    <div className="relative w-full">
                      <div className="aspect-[4/3] w-full bg-slate-50 overflow-hidden rounded-md">
                        {img ? (
                          <img
                            src={img}
                            alt={name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src =
                                "data:image/svg+xml;utf8," +
                                encodeURIComponent(
                                  `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='450'>
                                    <rect width='100%' height='100%' fill='#f3f4f6'/>
                                    <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#9ca3af' font-size='16'>
                                      No Image
                                    </text>
                                  </svg>`
                                );
                            }}
                          />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-slate-400 text-xs">
                            No Image
                          </div>
                        )}
                      </div>

                      {/* Price Tag */}
                      <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2">
                        <span className="inline-flex items-center rounded-full px-2 sm:px-2.5 py-1 text-[11px] sm:text-xs font-semibold bg-white/90 backdrop-blur border border-slate-200 text-slate-900 shadow-sm">
                          {price != null
                            ? formatINR(price)
                            : "Price on request"}
                        </span>
                      </div>
                    </div>

                    {/* Product Name */}
                    <div className="w-full">
                      <h3 className="font-medium text-slate-900 leading-snug line-clamp-2 text-[13px] sm:text-sm">
                        {name}
                      </h3>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AllProducts;
